import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardHeader, CardTitle, CardDescription, CardFooter, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  Activity,
  Heart,
  HeartPulse,
  Sparkles,
  HeartHandshake,
  Baby,
  Users,
  HandHeart,
  Stethoscope,
  Ribbon,
  Brain,
  Wind,
  Accessibility,
  Bone,
  Smile,
  Siren,
  Shield,
  CheckCircle2,
} from "lucide-react";

export interface MedicalCondition {
  id: string;
  name: string;
  slug: string;
  description: string;
  category: string;
  iconName: string;
  isFeatured: boolean;
  displayOrder: number;
}

export interface ConditionWallet {
  id: string;
  conditionId: string;
  title: string;
  summary: string;
  story: string;
  fundingGoal: number;
  totalRaised: number;
  memberCount: number;
  contributorCount: number;
  servicesDelivered: number;
  visibilityLevel: string;
  isFeatured: boolean;
  coverImageUrl: string | null;
  createdAt: string;
}

export interface WalletMembership {
  id: string;
  walletId: string;
  userId: string;
  status: "pending" | "active" | "suspended" | "revoked";
  membershipType: string;
  consentToShare: boolean;
  consentToContact: boolean;
  consentToPublic: boolean;
  createdAt: string;
}

export const iconMap: Record<string, any> = {
  Activity: Activity,
  Heart: Heart,
  HeartPulse: HeartPulse,
  Sparkles: Sparkles,
  HeartHandshake: HeartHandshake,
  Baby: Baby,
  Stethoscope: Stethoscope,
  Ribbon: Ribbon,
  Brain: Brain,
  Wind: Wind,
  Accessibility: Accessibility,
  Bone: Bone,
  Smile: Smile,
  Siren: Siren,
  Shield: Shield,
  HandHeart: HandHeart,
};

export function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

export function ConditionCard({ condition, walletCount }: { condition: MedicalCondition; walletCount: number }) {
  const { t } = useTranslation();
  const Icon = iconMap[condition.iconName] || Heart;
  
  return (
    <Link href={`/conditions/${condition.id}`}>
      <Card className="h-full hover-elevate cursor-pointer transition-all duration-200 border">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon className="w-6 h-6 text-primary" />
            </div>
            {condition.isFeatured && (
              <Badge variant="secondary" className="text-xs">{t('conditions.card.featured-badge')}</Badge>
            )}
          </div>
          <CardTitle className="text-lg mt-3">{condition.name}</CardTitle>
          <CardDescription className="line-clamp-2 text-sm">
            {condition.description}
          </CardDescription>
        </CardHeader>
        <CardFooter className="pt-0">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="w-4 h-4" />
            <span>{t('conditions.card.fund-count', { count: walletCount })}</span>
          </div>
        </CardFooter>
      </Card>
    </Link>
  );
}

const MONTHLY_AMOUNTS = [
  { label: "$0 — Membership only", cents: 0 },
  { label: "$10 / month", cents: 1000 },
  { label: "$25 / month", cents: 2500 },
  { label: "$50 / month", cents: 5000 },
];

function EnrollDialog({
  wallet,
  open,
  onClose,
}: {
  wallet: ConditionWallet;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [selectedCents, setSelectedCents] = useState(0);
  const [customAmount, setCustomAmount] = useState("");
  const [useCustom, setUseCustom] = useState(false);

  const { data: user } = useQuery<{ id: string } | null>({
    queryKey: ["/api/user"],
    retry: false,
  });

  const effectiveCents = useCustom
    ? Math.round((parseFloat(customAmount) || 0) * 100)
    : selectedCents;

  const enrollMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("login_required");
      const res = await apiRequest("POST", `/api/condition-wallets/${wallet.id}/enroll`, {
        monthlyAmount: effectiveCents,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: t('conditions.toast.enroll-success-title'),
        description:
          effectiveCents > 0
            ? `You've joined ${wallet.title} with a $${(effectiveCents / 100).toFixed(0)}/month pledge. Your application is pending review.`
            : `You've joined ${wallet.title}. Your application is pending review.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/condition-wallets", wallet.id, "membership"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user/condition-memberships"] });
      queryClient.invalidateQueries({ queryKey: ["/api/public/condition-wallets", wallet.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/public/condition-wallets"] });
      onClose();
    },
    onError: (error: any) => {
      if (error.message === "login_required") {
        toast({
          title: t('conditions.toast.login-required-title'),
          description: t('conditions.toast.login-required-body'),
          variant: "destructive",
        });
        setLocation("/auth");
        onClose();
        return;
      }
      toast({
        title: t('conditions.toast.enroll-error-title'),
        description: error.message || t('conditions.toast.enroll-error-body'),
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('conditions.dialog.join-title')}</DialogTitle>
          <DialogDescription>
            Enroll in <strong>{wallet.title}</strong> to receive healthcare funding through community support. Your application will be reviewed by fund administrators.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium">{t('conditions.dialog.membership-includes')}</p>
            <ul className="text-sm text-muted-foreground space-y-1.5">
              {[
                t('conditions.dialog.benefit-payments'),
                t('conditions.dialog.benefit-schedules'),
                t('conditions.dialog.benefit-support'),
              ].map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('conditions.dialog.monthly-contribution')}</Label>
            <p className="text-xs text-muted-foreground">
              Add a recurring pledge to help sustain the fund. You can set $0 for membership only.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {MONTHLY_AMOUNTS.map((opt) => (
                <Button
                  key={opt.cents}
                  type="button"
                  size="sm"
                  variant={!useCustom && selectedCents === opt.cents ? "default" : "outline"}
                  onClick={() => { setUseCustom(false); setSelectedCents(opt.cents); }}
                  data-testid={`button-pledge-${opt.cents}`}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Button
                type="button"
                size="sm"
                variant={useCustom ? "default" : "outline"}
                onClick={() => setUseCustom(true)}
                data-testid="button-pledge-custom"
              >
                Custom
              </Button>
              {useCustom && (
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground text-sm">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    placeholder="0"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className="w-24"
                    data-testid="input-pledge-custom"
                  />
                  <span className="text-muted-foreground text-sm">/ month</span>
                </div>
              )}
            </div>
            {effectiveCents > 0 && (
              <p className="text-xs text-primary font-medium">
                Pledging ${(effectiveCents / 100).toFixed(2)}/month
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} data-testid="button-cancel-enroll">
            Cancel
          </Button>
          <Button
            onClick={() => enrollMutation.mutate()}
            disabled={enrollMutation.isPending}
            data-testid="button-submit-enroll"
          >
            {enrollMutation.isPending ? t('conditions.dialog.enrolling') : t('conditions.dialog.enroll')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function WalletCard({ wallet }: { wallet: ConditionWallet }) {
  const { t } = useTranslation();
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const percentFunded = wallet.fundingGoal > 0
    ? Math.round((wallet.totalRaised / wallet.fundingGoal) * 100)
    : 0;

  return (
    <>
      <Card className="h-full flex flex-col border">
        <Link href={`/condition-wallets/${wallet.id}`} className="block flex-1 cursor-pointer hover-elevate rounded-t-md">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-2">
              <CardTitle className="text-lg line-clamp-2">{wallet.title}</CardTitle>
              {wallet.isFeatured && (
                <Badge variant="secondary" className="text-xs shrink-0">{t('conditions.card.featured-badge')}</Badge>
              )}
            </div>
            <CardDescription className="line-clamp-2 text-sm mt-2">
              {wallet.summary}
            </CardDescription>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="space-y-3">
              <div>
                <div className="flex justify-between gap-2 text-sm mb-1">
                  <span className="text-muted-foreground">{t('conditions.card.raised')}</span>
                  <span className="font-semibold">{formatCurrency(wallet.totalRaised)}</span>
                </div>
                <Progress value={percentFunded} className="h-2" />
                <div className="flex justify-between gap-2 text-xs text-muted-foreground mt-1">
                  <span>{t('conditions.card.percent-funded', { percent: percentFunded })}</span>
                  <span>Goal: {formatCurrency(wallet.fundingGoal)}</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                <div className="text-center">
                  <div className="text-lg font-semibold">{wallet.memberCount}</div>
                  <div className="text-xs text-muted-foreground">{t('conditions.card.members')}</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold">{wallet.contributorCount}</div>
                  <div className="text-xs text-muted-foreground">{t('conditions.card.supporters')}</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold">{wallet.servicesDelivered}</div>
                  <div className="text-xs text-muted-foreground">{t('conditions.card.funded')}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Link>

        <CardFooter className="pt-0 flex gap-2 border-t mt-auto">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => setShowEnrollDialog(true)}
            data-testid={`button-join-${wallet.id}`}
          >
            <Users className="w-4 h-4 mr-2" />
            {t('conditions.dialog.join-title')}
          </Button>
          <Button variant="default" className="flex-1" asChild>
            <Link href={`/condition-wallets/${wallet.id}`} data-testid={`button-contribute-${wallet.id}`}>
              <HandHeart className="w-4 h-4 mr-2" />
              {t('common.cta.support')}
            </Link>
          </Button>
        </CardFooter>
      </Card>

      <EnrollDialog
        wallet={wallet}
        open={showEnrollDialog}
        onClose={() => setShowEnrollDialog(false)}
      />
    </>
  );
}
